/**
 * useDragDropTags - 标签/标签组拖拽排序 Hook
 * 共享于本地剪藏簿和云剪藏簿
 */

import { useState, useCallback } from 'react';

// ============ 类型定义 ============

/** 基础标签接口约束 */
export interface BaseTag {
  id: string;
  name: string;
  groupId?: string | null;
  order: number;
}

/** 基础标签组接口约束 */
export interface BaseTagGroup {
  id: string;
  name: string;
  order: number;
  isExpanded?: boolean;
  tags?: BaseTag[];
}

/** 拖拽目标类型 */
export type DragTargetType = 'tag' | 'group' | 'group-area' | 'tag-in-group';

/** 拖拽项类型 */
export type DragItemType = 'tag' | 'group';

/** 拖拽项 */
export interface DraggedItem<TTag, TGroup> {
  type: DragItemType;
  id: string;
  data: TTag | TGroup;
}

/** 放置指示线位置 */
export interface DropIndicatorPosition {
  id: string;
  position: 'before' | 'after';
}

/** 拖拽处理器接口 */
export interface DragDropHandlers<TTag, TGroup> {
  onDragStart: (e: React.DragEvent, type: DragItemType, item: TTag | TGroup) => void;
  onDragOver: (e: React.DragEvent, targetType: DragTargetType, targetId: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, targetType: DragTargetType, targetId: string) => void;
  onDragEnd: () => void;
  // 状态（用于渲染指示线）
  draggedItem: DraggedItem<TTag, TGroup> | null;
  isDraggingOver: string | null;
  dropIndicatorPosition: DropIndicatorPosition | null;
}

/** Hook 配置选项 */
export interface UseDragDropTagsOptions<TTag extends BaseTag, TGroup extends BaseTagGroup> {
  tags: TTag[];
  tagGroups: TGroup[];

  /** 重新排序未分组标签 */
  onReorderUngroupedTags: (tagIds: string[]) => Promise<void>;

  /** 重新排序标签组 */
  onReorderGroups: (orders: Array<{ id: string; order: number }>) => Promise<void>;

  /** 移动标签到组（或移出组） */
  onMoveTagToGroup: (tagId: string, groupId: string | null, order: number) => Promise<void>;

  /** 重新排序组内标签 */
  onReorderTagsInGroup: (groupId: string, tagIds: string[]) => Promise<void>;

  /** 移动前拦截（本地端：检查分享状态） */
  onBeforeMove?: (tag: TTag, targetGroupId: string | null) => boolean | Promise<boolean>;

  /** 操作完成后回调 */
  onComplete?: () => void;

  /** 错误处理 */
  onError?: (error: Error, message: string) => void;
}

/** Hook 返回值 */
export interface UseDragDropTagsReturn<TTag extends BaseTag, TGroup extends BaseTagGroup> {
  handlers: DragDropHandlers<TTag, TGroup>;
  draggedItem: DraggedItem<TTag, TGroup> | null;
  isDraggingOver: string | null;
  dropIndicatorPosition: DropIndicatorPosition | null;
}

// ============ Hook 实现 ============

export function useDragDropTags<TTag extends BaseTag, TGroup extends BaseTagGroup>(
  options: UseDragDropTagsOptions<TTag, TGroup>
): UseDragDropTagsReturn<TTag, TGroup> {
  const {
    tags,
    tagGroups,
    onReorderUngroupedTags,
    onReorderGroups,
    onMoveTagToGroup,
    onReorderTagsInGroup,
    onBeforeMove,
    onComplete,
    onError,
  } = options;

  // ===== 状态 =====
  const [draggedItem, setDraggedItem] = useState<DraggedItem<TTag, TGroup> | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState<string | null>(null);
  const [dropIndicatorPosition, setDropIndicatorPosition] = useState<DropIndicatorPosition | null>(null);

  // ===== 辅助函数 =====

  /** 获取组内标签（按 order 排序）
   * 始终从 tags 数组过滤，确保乐观更新时能反映最新状态
   */
  const getGroupTags = useCallback((group: TGroup): TTag[] => {
    return tags
      .filter(t => t.groupId === group.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [tags]);

  /** 获取未分组标签（按 order 排序） */
  const getUngroupedTags = useCallback((): TTag[] => {
    return tags
      .filter(t => !t.groupId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [tags]);

  // ===== 事件处理器 =====

  /** 开始拖拽 */
  const handleDragStart = useCallback((
    e: React.DragEvent,
    type: DragItemType,
    item: TTag | TGroup
  ) => {
    setDraggedItem({ type, id: item.id, data: item });
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  /** 拖拽悬停 */
  const handleDragOver = useCallback((
    e: React.DragEvent,
    targetType: DragTargetType,
    targetId: string
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedItem) return;

    // 判断是否显示指示线
    const showIndicator =
      (draggedItem.type === 'tag' && targetType === 'tag') ||
      (draggedItem.type === 'tag' && targetType === 'tag-in-group') ||
      (draggedItem.type === 'group' && targetType === 'group-area');

    if (showIndicator && draggedItem.id !== targetId) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const mouseY = e.clientY;
      const elementMiddle = rect.top + rect.height / 2;
      const position = mouseY < elementMiddle ? 'before' : 'after';
      setDropIndicatorPosition({ id: targetId, position });
    } else if (targetType === 'group-area') {
      setIsDraggingOver(targetId);
      setDropIndicatorPosition(null);
    } else {
      setDropIndicatorPosition(null);
    }
  }, [draggedItem]);

  /** 拖拽离开 */
  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(null);
    setDropIndicatorPosition(null);
  }, []);

  /** 拖拽结束 */
  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setIsDraggingOver(null);
    setDropIndicatorPosition(null);
  }, []);

  /** 放置处理 */
  const handleDrop = useCallback(async (
    e: React.DragEvent,
    targetType: DragTargetType,
    targetId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItem) {
      handleDragEnd();
      return;
    }

    const currentDropPosition = dropIndicatorPosition;

    try {
      // ===== 场景 1: Tag 拖入 Group 区域（放到末尾） =====
      if (draggedItem.type === 'tag' && targetType === 'group-area') {
        const tag = tags.find(t => t.id === draggedItem.id);
        if (!tag) return;

        // 移动前拦截检查
        if (onBeforeMove) {
          const canMove = await onBeforeMove(tag, targetId);
          if (!canMove) return;
        }

        const groupTags = getGroupTags(tagGroups.find(g => g.id === targetId) as TGroup);
        const newOrder = groupTags.length;

        await onMoveTagToGroup(draggedItem.id, targetId, newOrder);
        onComplete?.();
      }

      // ===== 场景 2: Tag 从外部拖入 Group 内某个 Tag 位置 =====
      else if (
        draggedItem.type === 'tag' &&
        targetType === 'tag-in-group' &&
        (draggedItem.data as TTag).groupId !== tags.find(t => t.id === targetId)?.groupId
      ) {
        const draggedTag = tags.find(t => t.id === draggedItem.id);
        const targetTag = tags.find(t => t.id === targetId);
        if (!draggedTag || !targetTag || !targetTag.groupId) return;

        // 移动前拦截检查
        if (onBeforeMove) {
          const canMove = await onBeforeMove(draggedTag, targetTag.groupId);
          if (!canMove) return;
        }

        const targetGroup = tagGroups.find(g => g.id === targetTag.groupId);
        if (!targetGroup) return;

        const groupTags = getGroupTags(targetGroup);
        const targetIndex = groupTags.findIndex(t => t.id === targetId);

        let insertPosition = targetIndex;
        if (currentDropPosition?.position === 'after') {
          insertPosition += 1;
        }

        // 先移动到组
        await onMoveTagToGroup(draggedItem.id, targetTag.groupId, insertPosition);

        // 重新排序组内其他标签
        const newGroupTags = [...groupTags.filter(t => t.id !== draggedItem.id)];
        newGroupTags.splice(insertPosition, 0, draggedTag);
        await onReorderTagsInGroup(targetTag.groupId, newGroupTags.map(t => t.id));

        onComplete?.();
      }

      // ===== 场景 3: Tag 从 Group 移出到未分组区域 =====
      else if (
        draggedItem.type === 'tag' &&
        targetType === 'tag' &&
        (draggedItem.data as TTag).groupId
      ) {
        const draggedTag = tags.find(t => t.id === draggedItem.id);
        if (!draggedTag) return;

        const ungroupedTags = getUngroupedTags();
        const targetIndex = ungroupedTags.findIndex(t => t.id === targetId);
        let newOrder = targetIndex >= 0 ? targetIndex : ungroupedTags.length;

        if (currentDropPosition?.position === 'after') {
          newOrder += 1;
        }

        // 移出组
        await onMoveTagToGroup(draggedItem.id, null, newOrder);

        // 重新排序未分组标签
        const newUngroupedTags = [...ungroupedTags];
        newUngroupedTags.splice(newOrder, 0, draggedTag);
        await onReorderUngroupedTags(newUngroupedTags.map(t => t.id));

        onComplete?.();
      }

      // ===== 场景 4: 未分组 Tag 之间排序 =====
      else if (
        draggedItem.type === 'tag' &&
        targetType === 'tag' &&
        !(draggedItem.data as TTag).groupId
      ) {
        if (draggedItem.id === targetId) return;

        const ungroupedTags = getUngroupedTags();
        const draggedIndex = ungroupedTags.findIndex(t => t.id === draggedItem.id);
        let targetIndex = ungroupedTags.findIndex(t => t.id === targetId);

        if (draggedIndex === -1 || targetIndex === -1) return;

        if (currentDropPosition?.position === 'after') {
          targetIndex += 1;
        }

        if (targetIndex > draggedIndex) {
          targetIndex -= 1;
        }

        // 重新排列
        const reordered = [...ungroupedTags];
        const [movedTag] = reordered.splice(draggedIndex, 1);
        reordered.splice(targetIndex, 0, movedTag);

        await onReorderUngroupedTags(reordered.map(t => t.id));
        onComplete?.();
      }

      // ===== 场景 5: Group 之间排序 =====
      else if (draggedItem.type === 'group' && targetType === 'group-area') {
        if (draggedItem.id === targetId) return;

        const draggedIndex = tagGroups.findIndex(g => g.id === draggedItem.id);
        let targetIndex = tagGroups.findIndex(g => g.id === targetId);

        if (draggedIndex === -1 || targetIndex === -1) return;

        if (currentDropPosition?.position === 'after') {
          targetIndex += 1;
        }

        if (targetIndex > draggedIndex) {
          targetIndex -= 1;
        }

        // 重新排列
        const reordered = [...tagGroups];
        const [movedGroup] = reordered.splice(draggedIndex, 1);
        reordered.splice(targetIndex, 0, movedGroup);

        const orders = reordered.map((g, idx) => ({ id: g.id, order: idx }));
        await onReorderGroups(orders);
        onComplete?.();
      }

      // ===== 场景 6: Group 内 Tag 之间排序 =====
      else if (draggedItem.type === 'tag' && targetType === 'tag-in-group') {
        if (draggedItem.id === targetId) return;

        const draggedTag = tags.find(t => t.id === draggedItem.id);
        const targetTag = tags.find(t => t.id === targetId);

        if (!draggedTag || !targetTag || draggedTag.groupId !== targetTag.groupId) return;

        const groupId = draggedTag.groupId;
        if (!groupId) return;

        const group = tagGroups.find(g => g.id === groupId);
        if (!group) return;

        const groupTags = getGroupTags(group);
        const draggedIndex = groupTags.findIndex(t => t.id === draggedItem.id);
        let targetIndex = groupTags.findIndex(t => t.id === targetId);

        if (draggedIndex === -1 || targetIndex === -1) return;

        if (currentDropPosition?.position === 'after') {
          targetIndex += 1;
        }

        if (targetIndex > draggedIndex) {
          targetIndex -= 1;
        }

        // 重新排列
        const reordered = [...groupTags];
        const [movedTag] = reordered.splice(draggedIndex, 1);
        reordered.splice(targetIndex, 0, movedTag);

        await onReorderTagsInGroup(groupId, reordered.map(t => t.id));
        onComplete?.();
      }
    } catch (error) {
      onError?.(error as Error, '拖拽操作失败');
    } finally {
      handleDragEnd();
    }
  }, [
    draggedItem,
    dropIndicatorPosition,
    tags,
    tagGroups,
    getGroupTags,
    getUngroupedTags,
    onBeforeMove,
    onMoveTagToGroup,
    onReorderUngroupedTags,
    onReorderGroups,
    onReorderTagsInGroup,
    onComplete,
    onError,
    handleDragEnd,
  ]);

  // ===== 返回 =====
  return {
    handlers: {
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      onDragEnd: handleDragEnd,
      draggedItem,
      isDraggingOver,
      dropIndicatorPosition,
    },
    draggedItem,
    isDraggingOver,
    dropIndicatorPosition,
  };
}

export default useDragDropTags;
